import React from 'react';

/**
 * Custom Miami Car Queens icon set.
 *
 * Single-weight line drawings on a 24×24 grid, stroke only, round caps and joins —
 * the same drawing language as the gold bow motif. They replace the generic library
 * icons so the page has one consistent hand.
 *
 * All of them inherit `currentColor`, so color is controlled with text utilities.
 */

interface IconProps {
  className?: string;
  strokeWidth?: number;
}
function Svg({
  className = 'h-5 w-5',
  strokeWidth = 1.5,
  children
}: IconProps & {
  children: React.ReactNode;
}) {
  return <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false" className={className} stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" data-id="element-194" data-mp="393hrqnhsvi8p">
      {children}
    </svg>;
}

/** VIP service */
export function IconCrown(props: IconProps) {
  return <Svg {...props} data-id="element-195" data-mp="y8axqsena8n1">
      <path d="M3.2 7.4 6 13l3-7.2 3 5.2 3-5.2L18 13l2.8-5.6-1.4 10.2H4.6Z" data-id="element-196" data-mp="y7ssx7dx2m3i" />
      <path d="M4.6 20.2h14.8" data-id="element-197" data-mp="109m4j3r7nfqh" />
    </Svg>;
}

/* ── Pain-point strip. Each one draws the friction it removes. ───────────────── */

/** No trip to the dealership just to ask — a clock turning back on itself. */
export function IconNoTrip(props: IconProps) {
  return <Svg {...props} data-id="element-198" data-mp="mbe3okr1zua1">
      <path d="M20.3 12a8.3 8.3 0 1 1-2.6-6" data-id="element-199" data-mp="m9t9wxckcfd8" />
      <path d="M18.2 2.9v3.4h-3.4" data-id="element-200" data-mp="md8p0068loen" />
      <path d="M12 7.4V12l3 1.8" data-id="element-201" data-mp="hc1hm280ar8i" />
    </Svg>;
}

/** No negotiating on your own — an open palm, the universal "hold on". */
export function IconNoHaggle(props: IconProps) {
  return <Svg {...props} data-id="element-202" data-mp="8j7xnrhm73ob">
      <path d="M9.4 11.2V4.6a1.3 1.3 0 0 1 2.6 0v6" data-id="element-203" data-mp="8kj2zvqpkjhj" />
      <path d="M12 10.2V3.9a1.3 1.3 0 0 1 2.6 0v6.3" data-id="element-204" data-mp="8h3keixtssbd" />
      <path d="M14.6 10.6V5.2a1.3 1.3 0 0 1 2.6 0v7.2" data-id="element-205" data-mp="3lo9nzv47cj6" />
      <path d="M9.4 11.2V8.4a1.3 1.3 0 0 0-2.6 0v6.1" data-id="element-206" data-mp="3uqoa251z7lnm" />
      <path d="M6.8 13.6c0 4 2.6 6.8 6 6.8s4.6-2.4 4.6-5.6" data-id="element-207" data-mp="3uq6aozk0orbh" />
    </Svg>;
}

/** No cold calls, no pressure — a handset struck through. */
export function IconNoCalls(props: IconProps) {
  return <Svg {...props} data-id="element-208" data-mp="3g2eaxz6jyzci">
      <path d="M8.6 4.6 10.4 8l-1.9 1.6a12.4 12.4 0 0 0 5.5 5.4l1.6-1.9 3.4 1.8-.5 2.6a1.6 1.6 0 0 1-1.7 1.3A15.6 15.6 0 0 1 4.7 6.8 1.6 1.6 0 0 1 6 5.1Z" data-id="element-209" data-mp="3g620yocsh3mh" />
      <path d="M3.6 3.6 20.4 20.4" data-id="element-210" data-mp="374ccnre7eksx" />
    </Svg>;
}

/** No wasted time — a check inside a circle. */
export function IconNoWait(props: IconProps) {
  return <Svg {...props} data-id="element-211" data-mp="2p2vmmrx4p16">
      <circle cx="12" cy="12" r="8.4" data-id="element-212" data-mp="2ot0kks0nrom" />
      <path d="M8.4 12.2 11 14.8l4.6-5" data-id="element-213" data-mp="2puohpu4s8nr" />
    </Svg>;
}

/** Financing options */
export function IconWallet(props: IconProps) {
  return <Svg {...props} data-id="element-214" data-mp="3q2eu9l4f9fgp">
      <path d="M3.5 8.2h17v10.3a1.5 1.5 0 0 1-1.5 1.5H5a1.5 1.5 0 0 1-1.5-1.5Z" data-id="element-215" data-mp="3q2ydr69q0bwu" />
      <path d="M3.5 8.2 6 4.4a1.4 1.4 0 0 1 1.2-.6h9.6a1.4 1.4 0 0 1 1.2.6l2.5 3.8" data-id="element-216" data-mp="3u38jqixij2bp" />
      <path d="M15.6 13.6h3.4" data-id="element-217" data-mp="lnyn3qce6pbb" />
    </Svg>;
}

/** New · Used · Leasing */
export function IconCar(props: IconProps) {
  return <Svg {...props} data-id="element-218" data-mp="qfp0y6ajfv7h">
      <path d="M4.2 15.4 5.9 9.9A2.2 2.2 0 0 1 8 8.4h8a2.2 2.2 0 0 1 2.1 1.5l1.7 5.5" data-id="element-219" data-mp="qg87yryngz6q" />
      <path d="M4.2 15.4h15.6v3.1h-2.5v-1.4H6.7v1.4H4.2Z" data-id="element-220" data-mp="2b3rf79bwmul0" />
      <path d="M7.4 12.5h9.2" data-id="element-221" data-mp="266935a2lbz8s" />
    </Svg>;
}

/** Trade-in / price match */
export function IconSwap(props: IconProps) {
  return <Svg {...props} data-id="element-222" data-mp="2ktu9dcuriekx">
      <path d="M4 9h13.5l-3-3" data-id="element-223" data-mp="2ku3y6x42mfp1" />
      <path d="M20 15H6.5l3 3" data-id="element-224" data-mp="2fvsmshy4nx4p" />
    </Svg>;
}

/** Step 01 — tell us what you need */
export function IconChat(props: IconProps) {
  return <Svg {...props} data-id="element-225" data-mp="1s6zo4dq4fb6e">
      <path d="M4 6.4a1.6 1.6 0 0 1 1.6-1.6h12.8A1.6 1.6 0 0 1 20 6.4v8a1.6 1.6 0 0 1-1.6 1.6H9.2L5 19.6V16h-1.6" data-id="element-226" data-mp="1s6ginwhnqf99" />
      <path d="M8 9.4h8M8 12.2h5" data-id="element-227" data-mp="1n9hoxf1woabl" />
    </Svg>;
}

/** Step 02 — explore your options */
export function IconCompare(props: IconProps) {
  return <Svg {...props} data-id="element-228" data-mp="1x49m4hrksv5u">
      <path d="M11 4.6v14.8" data-id="element-229" data-mp="1x3q4711xe6rt" />
      <path d="M4.4 8.2h13.2" data-id="element-230" data-mp="1x7nnpnsf10c8" />
      <path d="M4.4 8.2 2.6 13a2.6 2.6 0 0 0 3.6 0Z" data-id="element-231" data-mp="3dj1cyq92pud1" />
      <path d="M17.6 8.2 15.8 13a2.6 2.6 0 0 0 3.6 0Z" data-id="element-232" data-mp="3dlnlas3sgdwb" />
      <path d="M7.6 19.4h6.8" data-id="element-233" data-mp="38jer5sf6j5sm" />
    </Svg>;
}

/** Step 03 — pick up your keys */
export function IconKey(props: IconProps) {
  return <Svg {...props} data-id="element-234" data-mp="1i5fhtdukfnd7">
      <circle cx="8.2" cy="8.4" r="3.6" data-id="element-235" data-mp="1n2fpft72cy6f" />
      <path d="M10.8 11 19 19.2" data-id="element-236" data-mp="1n3gyjs4irali" />
      <path d="M16.4 16.6l1.8-1.8M14 14.2l1.8-1.8" data-id="element-237" data-mp="1n4jpu15r7f1y" />
    </Svg>;
}

/** Kia factory warranty */
export function IconShield(props: IconProps) {
  return <Svg {...props} data-id="element-238" data-mp="1w3dri5xxyvmd">
      <path d="M12 3.4 19 6v5.2c0 4.4-2.9 7.6-7 9.4-4.1-1.8-7-5-7-9.4V6Z" data-id="element-239" data-mp="1w4oyeemoojhp" />
      <path d="m9.2 11.8 2 2 3.6-3.8" data-id="element-240" data-mp="21303yd5y47u4" />
    </Svg>;
}
export function IconWhatsApp(props: IconProps) {
  return <Svg {...props} data-id="element-241" data-mp="2600bksig1inc">
      <path d="M20 11.6a8 8 0 0 1-11.9 7L4 20l1.4-4A8 8 0 1 1 20 11.6Z" data-id="element-242" data-mp="25zgs37d5am77" />
      <path d="M9.3 8.6c.2-.4.4-.4.7-.4h.5c.2 0 .4 0 .6.5l.7 1.6c.1.2 0 .4-.1.6l-.4.5c-.1.2-.2.3 0 .6a6 6 0 0 0 2.6 2.2c.3.1.4 0 .6-.1l.5-.6c.2-.2.3-.2.6-.1l1.5.8c.3.1.4.3.4.5a1.7 1.7 0 0 1-1.7 1.6 7 7 0 0 1-5.9-5.7 2.7 2.7 0 0 1 .4-2Z" data-id="element-243" data-mp="2atb9u596wr3n" />
    </Svg>;
}
export function IconArrowRight(props: IconProps) {
  return <Svg {...props} data-id="element-244" data-mp="2os2n5pg8a8qq">
      <path d="M4.5 12h14" data-id="element-245" data-mp="2opgefmhlpn6x" />
      <path d="m13.5 6.8 5.2 5.2-5.2 5.2" data-id="element-246" data-mp="2yw9aool6x6lo" />
    </Svg>;
}
export function IconCheck(props: IconProps) {
  return <Svg {...props} data-id="element-247" data-mp="2tvlk240virrz">
      <path d="m4.8 12.6 4.6 4.6L19.2 6.8" data-id="element-248" data-mp="2tv23orqvgbg2" />
    </Svg>;
}
export function IconClose(props: IconProps) {
  return <Svg {...props} data-id="element-249" data-mp="2ozmr034loll8">
      <path d="M6 6l12 12M18 6 6 18" data-id="element-250" data-mp="2kvyklnrpfw1k" />
    </Svg>;
}
export function IconPlus(props: IconProps) {
  return <Svg {...props} data-id="element-251" data-mp="3hmqzii2wji1r">
      <path d="M12 5v14M5 12h14" data-id="element-252" data-mp="3hl63eo0xvr1s" />
    </Svg>;
}
export function IconMinus(props: IconProps) {
  return <Svg {...props} data-id="element-253" data-mp="3cofqg9kflbkl">
      <path d="M5 12h14" data-id="element-254" data-mp="3cnwh3h8ujvi6" />
    </Svg>;
}
export function IconAlert(props: IconProps) {
  return <Svg {...props} data-id="element-255" data-mp="37ulbfjfrtkru">
      <path d="M12 4.4 21 19.6H3Z" data-id="element-256" data-mp="37u1ti2q4ewdt" />
      <path d="M12 10v4.2M12 17h.01" data-id="element-257" data-mp="33vm6hyu1ho0e" />
    </Svg>;
}
export function IconInstagram(props: IconProps) {
  return <Svg {...props} data-id="element-258" data-mp="484eh0b9hpwo">
      <rect x="4" y="4" width="16" height="16" rx="4.6" data-id="element-259" data-mp="48m9xuq3o03j" />
      <circle cx="12" cy="12" r="3.6" data-id="element-260" data-mp="3vlqltj2xzkur" />
      <path d="M16.6 7.4h.01" data-id="element-261" data-mp="3vkpcpk5hl8fo" />
    </Svg>;
}
export function IconImage(props: IconProps) {
  return <Svg {...props} data-id="element-262" data-mp="1tqjggpl50mrr">
      <rect x="3.6" y="5" width="16.8" height="14" rx="2" data-id="element-263" data-mp="1ynjkyw2c9hgr" />
      <path d="m4.6 16.4 4.2-4.2 3.4 3.4 2.6-2.6 4.6 4.6" data-id="element-264" data-mp="1yn065o7zj96y" />
      <circle cx="15.4" cy="9.4" r="1.2" data-id="element-265" data-mp="1jz6lhp41lghi" />
    </Svg>;
}